<template lang="pug">
#room-schedule-list
  .box
    .box-header
      .d-flex.justify-content-start.align-items-center
        .d-flex.justify-content-start.flex-column
          .title
            span 空床スケジュール
          .note
            span 部屋情報と利用者の入退所日を基に部屋スケジュールを作成します。転床／仮押さえ登録と、ケアマネ等への空室／空床情報配信ができます。
        .d-flex.justify-content-end.align-items-start.align-self-stretch.flex-fill
          button.btn.setting-btn.mr-2(type="button" @click="gotoSetting") 公開設定
          button.btn.notification-btn(type="button" @click="onNotification") 更新通知
    .box-body
      b-tabs(v-model="tabIndex" @changed="changedTab" ref="tabs")
        // room inout schedules
        b-tab.tab(title="入退所・送迎予定")
          ResidentInOutScheduleListTab(ref="residentInOutScheduleListTab" @onEdit="gotoResidentEdit")
        // use type 1
        b-tab.tab(title="スケジュール（入居用）")
          RoomsSchedulesTab(ref="normalStayRoomScheduleTab" :useType="1")
        // use type 2
        b-tab.tab(title="スケジュール（ショートステイ）")
          RoomsSchedulesTab(ref="shortStayRoomScheduleTab" :useType="2")
        // move room schedules
        b-tab.tab(title="転床／仮押さえ一覧")
          MoveRoomScheduleListTab(ref="moveRoomScheduleListTab"
            :errorMessage="moveRoomScheduleListTabErrorMessage"
            @onAdd="onAddMoveRoomSchedule"
            @onEdit="onEditMoveRoomSchedule"
            @onDelete="confirmDeleteMoveRoomSchedule")
        // edit otherUserGroupMember permittedFunctions
        b-tab.tab(title="空室／空床情報の公開先")
          OtherUserGroupMemberVacancyEditTab(ref="otherUserGroupMemberVacancyEditTab"
            type="vacancy"
            :errorMessage="otherUserGroupMemberVacancyEditTabErrorMessage" @onUpdate="confirmUpdateVacancyFunction")
        // register and edit move room schedules
        b-tab.tab(v-for="tab in tabs" :key="'tab-' + tab.no" :ref="'moveRoomScheduleTab' + tab.no")
          template(v-slot:title)
            .tab-title
              span.text-truncate.d-inline-block.mr-2 {{ tab.title }}
              button.close(type="button" @click="closeMoveRoomScheduleTab(tab.no, false)") x
          MoveRoomScheduleRegisterTab(
            v-if="tab.no == 0"
            @onCancel="closeMoveRoomScheduleTab(tab.no, true)" @onRegister="confirmRegisterMoveRoomSchedule")
          MoveRoomScheduleEditTab(
            v-if="tab.no == 1"
            :beforeStartDatetime="moveRoomShcedulesUpdateStartDatetime" :beforeRoomId="moveRoomShcedulesUpdateRoomId"
            :beforeResidentId="moveRoomShcedulesUpdateResidentId"
            @onCancel="closeMoveRoomScheduleTab(tab.no, true)" @onUpdate="confirmUpdateMoveRoomSchedule")

  // move room schedules register modals
  OkCancelModal(v-model="showMoveRoomScheduleRegisterModal" title="転床／仮押さえ情報を登録しますか？" @ok="registerMoveRoomSchedule")
  OkModal(v-model="showMoveRoomScheduleRegisterSuccessModal" title="登録に成功しました。" @ok="refreshTabs")
  
  // move room schedules update modals
  OkCancelModal(v-model="showMoveRoomScheduleUpdateModal" title="転床／仮押さえ情報を更新しますか？" @ok="updateMoveRoomSchedule")
  OkModal(v-model="showMoveRoomScheduleUpdateSuccessModal" title="更新に成功しました。" @ok="refreshTabs")

  // move room schedules delete modals
  OkCancelModal(v-model="showMoveRoomScheduleDeleteModal" title="転床／仮押さえ情報を削除しますか？" @ok="deleteMoveRoomSchedule")
  OkModal(v-model="showMoveRoomScheduleDeleteSuccessModal" title="削除に成功しました。" @ok="refreshTabs")

  // otherUserGroupMember update modals
  OkCancelModal(v-model="showVacancyFunctionUpdateModal" title="空室／空床情報の公開先を更新しますか？" @ok="updateVacancyFunction")
  OkModal(v-model="showVacancyFunctionUpdateSuccessModal" title="更新に成功しました。" @ok="refreshTabs")

  // notification room schedule
  OkCancelModal(v-model="showNotificationRoomScheduleModal" title="空室／空床情報の更新通知を公開先メンバーに送信しますか？" @ok="notificationRoomSchedule")
  OkModal(v-model="showNotificationRoomScheduleSuccessModal" title="更新通知の送信を受け付けました。" @ok="refreshTabs")
  
  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Ref, Vue } from "vue-property-decorator";
import { Route } from "vue-router";
import { userStore } from "@/store/store";
import { BTab } from "bootstrap-vue";

import { PcUrl } from "@/commons/constants/PcUrl";
import MoveRoomSchedulesService, { MoveRoomSchedulesPatchModel, MoveRoomSchedulesPostModel, MoveRoomSchedulesPostUrlParam } from "@/commons/services/MoveRoomSchedules";
import OtherUserGroupModel from "@/commons/models/OtherUserGroup";
import OtherUserGroupMembersService, { OtherUserGroupMembersPatchModel } from "@/commons/services/OtherUserGroupMembers";
import NotificationRoomSchedulesService from "@/commons/services/NotificationRoomSchedules";

import RoomsSchedulesTab from "@/pc/containers/roomSchedules/RoomsSchedulesTab.vue";
import MoveRoomScheduleListTab from "@/pc/containers/roomSchedules/MoveRoomScheduleListTab.vue";
import { MoveRoomScheduleTableItem } from "@/pc/containers/roomSchedules/MoveRoomScheduleTable.vue";
import MoveRoomScheduleRegisterTab from "@/pc/containers/roomSchedules/MoveRoomScheduleRegisterTab.vue";
import MoveRoomScheduleEditTab from "@/pc/containers/roomSchedules/MoveRoomScheduleEditTab.vue";
import OtherUserGroupMemberVacancyEditTab from "@/pc/containers/roomSchedules/OtherUserGroupMemberVacancyEditTab.vue";
import ResidentInOutScheduleListTab from "@/pc/containers/roomSchedules/ResidentInOutScheduleListTab.vue";

import PageLoading from "@/pc/basics/PageLoading.vue";
import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import { ResidentInOutScheduleTableItem } from "@/pc/containers/roomSchedules/ResidentInOutScheduleTable.vue";

/**
 * ### フックの追加
 *
 * - 前画面のURLを見るためにbeforeRouteEnterを追加する
 */
Component.registerHooks(["beforeRouteEnter"]);

/**
 * ### 空床情報一覧コンポーネント
 */
@Component({
  components: {
    OkCancelModal,
    OkModal,
    PageLoading,
    RoomsSchedulesTab,
    MoveRoomScheduleListTab,
    MoveRoomScheduleRegisterTab,
    MoveRoomScheduleEditTab,
    OtherUserGroupMemberVacancyEditTab,
    ResidentInOutScheduleListTab
  }
})
export default class RoomScheduleList extends Vue {
  /** 施設関係者グループメンバーサービス */
  @Inject("OtherUserGroupMembersService") otherUserGroupMembersService!: OtherUserGroupMembersService;
  /** 転床サービス */
  @Inject("MoveRoomSchedulesService") moveRoomSchedulesService!: MoveRoomSchedulesService;
  /** 空室／空床情報の更新通知サービス */
  @Inject("NotificationRoomSchedulesService") notificationRoomSchedulesService!: NotificationRoomSchedulesService;

  @Ref()
  residentInOutScheduleListTab!: ResidentInOutScheduleListTab;
  @Ref()
  normalStayRoomScheduleTab!: RoomsSchedulesTab;
  @Ref()
  shortStayRoomScheduleTab!: RoomsSchedulesTab;
  @Ref()
  moveRoomScheduleListTab!: MoveRoomScheduleListTab;
  @Ref()
  otherUserGroupMemberVacancyEditTab!: OtherUserGroupMemberVacancyEditTab;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ページ全体ローディングメッセージ */
  loadingMessage: string = "読込み中...";
  /** 前画面URL */
  fromUrl: string = "";

  /** タブインデックス(0始まり) */
  tabIndex: number = 0;
  /** 動的追加タブ */
  tabs: Array<Tab> = [];

  /** 仮押さえ／転床一覧タブエラーメッセージ */
  moveRoomScheduleListTabErrorMessage: string = "";

  /** 仮押さえ／転床登録確認モーダル表示可否 */
  showMoveRoomScheduleRegisterModal: boolean = false;
  /** 仮押さえ／転床登録対象の転床情報 */
  registerTargetMoveRoomSchedule: { urlPatam: MoveRoomSchedulesPostUrlParam; requestBody: MoveRoomSchedulesPostModel } | null = null;
  /** 仮押さえ／転床登録完了モーダル表示可否 */
  showMoveRoomScheduleRegisterSuccessModal: boolean = false;

  /** 仮押さえ／転床更新 対象開始日時 */
  moveRoomShcedulesUpdateStartDatetime: number | null = null;
  /** 仮押さえ／転床更新 対象部屋ID */
  moveRoomShcedulesUpdateRoomId: number | null = null;
  /** 仮押さえ／転床更新 対象利用者ID */
  moveRoomShcedulesUpdateResidentId: number | null = null;
  /** 仮押さえ／転床更新確認モーダル表示可否 */
  showMoveRoomScheduleUpdateModal: boolean = false;
  /** 仮押さえ／転床更新対象の転床情報 */
  updateTargetMoveRoomSchedule: { urlPatam: MoveRoomSchedulesPostUrlParam; requestBody: MoveRoomSchedulesPatchModel } | null = null;
  /** 仮押さえ／転床更新完了モーダル表示可否 */
  showMoveRoomScheduleUpdateSuccessModal: boolean = false;

  /** 仮押さえ／転床削除確認モーダル表示可否 */
  showMoveRoomScheduleDeleteModal: boolean = false;
  /** 仮押さえ／転床削除対象の転床情報 */
  deleteTargetMoveRoomSchedule: MoveRoomScheduleTableItem | null = null;
  /** 仮押さえ／転床削除完了モーダル表示可否 */
  showMoveRoomScheduleDeleteSuccessModal: boolean = false;

  /** 「空室情報の公開先」機能使用可否更新モーダル表示可否 */
  showVacancyFunctionUpdateModal: boolean = false;
  /** 更新対象の「空室情報の公開先」機能使用可否を持つ施設関係者グループメンバー */
  updateVacancyFunctionTargetMembers: Array<OtherUserGroupModel> = [];
  /** 「空室情報の公開先」機能使用可否更新完了モーダル表示可否 */
  showVacancyFunctionUpdateSuccessModal: boolean = false;
  /** 「空室情報の公開先」機能使用可否タブエラーメッセージ */
  otherUserGroupMemberVacancyEditTabErrorMessage: string = "";

  /** 空室／空床情報の更新通知確認モーダル表示可否 */
  showNotificationRoomScheduleModal: boolean = false;
  /** 空室／空床情報の更新通知完了モーダル表示可否 */
  showNotificationRoomScheduleSuccessModal: boolean = false;

  /**
   * ### 画面遷移前フックイベント
   *
   * - 前画面のnameを見て、利用者編集(部屋スケジュールからの遷移)の場合は「入退所・送迎予定」タブを開くように初期値を設定する
   */
  public beforeRouteEnter(to: any, from: any, next: Function): void {
    if (PcUrl.RoomScheduleResidentEdit == from.name) {
      next((component: RoomScheduleList) => {
        component.tabIndex = 0;
      });
    } else {
      next();
    }
  }

  /**
   * ### 仮押さえ／転床の削除確認モーダルを表示する
   */
  public confirmDeleteMoveRoomSchedule(moveRoomSchedule: MoveRoomScheduleTableItem): void {
    this.deleteTargetMoveRoomSchedule = moveRoomSchedule;
    this.showMoveRoomScheduleDeleteModal = true;

    return;
  }

  /**
   * ### 仮押さえ／転床情報を削除する
   *
   * - 画面全体ローディングを表示する
   * - 仮押さえ／転床情報を削除する
   * - 削除成功モーダルを表示する
   * - ページ情報を取得しなおす
   */
  public async deleteMoveRoomSchedule(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "削除中...";

    const moveRoomSchedule: MoveRoomScheduleTableItem = this.deleteTargetMoveRoomSchedule as MoveRoomScheduleTableItem;
    await this.moveRoomSchedulesService.delete(userStore, moveRoomSchedule.startDatetime, moveRoomSchedule.roomId, moveRoomSchedule.residentId);

    this.showMoveRoomScheduleDeleteSuccessModal = true;
    this.isBusy = false;

    return;
  }

  /**
   * ### 仮押さえ／転床登録を新規タブで開く
   *
   * - エラーメッセージ初期化
   * - 既にタブが追加されている場合はエラーメッセージを追加してタブ追加しない
   * - 仮押さえ／転床登録タブを追加する
   *   - 先頭に追加
   * - 追加したタブをアクティブにするためにタブに追加したコンポーネントを保持する
   */
  public onAddMoveRoomSchedule(): void {
    this.moveRoomScheduleListTabErrorMessage = "";

    for (const tab of this.tabs) {
      if (tab.no == TabNo.MOVE_ROOM_SCHEDULE_REGISTER) {
        this.moveRoomScheduleListTabErrorMessage = "既に転床／仮押さえ情報登録タブが開かれているため、新規にタブを開けません";
        return;
      }
    }

    this.tabs.push({
      no: TabNo.MOVE_ROOM_SCHEDULE_REGISTER,
      title: "転床／仮押さえ登録"
    });

    return;
  }

  /**
   * ### 仮押さえ／転床登録の確認モーダルを表示する
   *
   * - 登録対象の情報を保持する
   */
  public confirmRegisterMoveRoomSchedule(moveRoomSchedule: { urlPatam: MoveRoomSchedulesPostUrlParam; requestBody: MoveRoomSchedulesPostModel }): void {
    this.registerTargetMoveRoomSchedule = moveRoomSchedule;
    this.showMoveRoomScheduleRegisterModal = true;

    return;
  }

  /**
   * ### 仮押さえ／転床情報を登録する
   *
   * - 全体ローディングモーダルを表示する
   * - 仮押さえ／転床を登録する
   * - 全体ローディングモーダルを非表示にする
   * - 完了モーダルを表示する
   * - 仮押さえ／転床登録タブを削除する
   * - 仮押さえ／転床一覧タブをアクティブにするために次のアクティブタブ名を格納する
   */
  public async registerMoveRoomSchedule(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "登録中...";

    const param: { urlPatam: MoveRoomSchedulesPostUrlParam; requestBody: MoveRoomSchedulesPostModel } = this.registerTargetMoveRoomSchedule as {
      urlPatam: MoveRoomSchedulesPostUrlParam;
      requestBody: MoveRoomSchedulesPostModel;
    };
    await this.moveRoomSchedulesService.post(userStore, param.urlPatam.startDatetime, param.urlPatam.roomId, param.urlPatam.residentId, param.requestBody);

    this.isBusy = false;
    this.showMoveRoomScheduleRegisterSuccessModal = true;

    this.closeMoveRoomScheduleTab(0, true);

    return;
  }

  /**
   * ### 仮押さえ／転床編集を新規タブで開く
   *
   * - エラーメッセージ初期化
   * - 既にタブが追加されている場合はエラーメッセージを追加してタブ追加しない
   * - 仮押さえ／転床編集タブで使用する開始日時、部屋ID、利用者IDを保持する
   * - 仮押さえ／転床編集タブを追加する
   * - 追加したタブをアクティブにするためにタブに追加したコンポーネントを保持する
   */
  public onEditMoveRoomSchedule(moveRoomSchedule: MoveRoomScheduleTableItem): void {
    this.moveRoomScheduleListTabErrorMessage = "";

    for (const tab of this.tabs) {
      if (tab.no == TabNo.MOVE_ROOM_SCHEDULE_EDIT) {
        this.moveRoomScheduleListTabErrorMessage = "既に転床／仮押さえ情報修正タブが開かれているため、新規にタブを開けません";
        return;
      }
    }

    this.moveRoomShcedulesUpdateStartDatetime = moveRoomSchedule.startDatetime;
    this.moveRoomShcedulesUpdateRoomId = moveRoomSchedule.roomId;
    this.moveRoomShcedulesUpdateResidentId = moveRoomSchedule.residentId;

    this.tabs.push({
      no: TabNo.MOVE_ROOM_SCHEDULE_EDIT,
      title: "転床／仮押さえ修正"
    });

    return;
  }

  /**
   * ### 仮押さえ／転床更新の確認モーダルを表示する
   *
   * - 更新対象の情報を保持する
   */
  public confirmUpdateMoveRoomSchedule(moveRoomSchedule: { urlPatam: MoveRoomSchedulesPostUrlParam; requestBody: MoveRoomSchedulesPatchModel }): void {
    this.updateTargetMoveRoomSchedule = moveRoomSchedule;
    this.showMoveRoomScheduleUpdateModal = true;

    return;
  }

  /**
   * ### 仮押さえ／転床情報を更新する
   *
   * - 全体ローディングモーダルを表示する
   * - 仮押さえ／転床を更新する
   * - 全体ローディングモーダルを非表示にする
   * - 完了モーダルを表示する
   * - 仮押さえ／転床更新タブを削除する
   * - 仮押さえ／転床一覧タブをアクティブにするために次のアクティブタブ名を格納する
   */
  public async updateMoveRoomSchedule(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    const param: { urlPatam: MoveRoomSchedulesPostUrlParam; requestBody: MoveRoomSchedulesPatchModel } = this.updateTargetMoveRoomSchedule as {
      urlPatam: MoveRoomSchedulesPostUrlParam;
      requestBody: MoveRoomSchedulesPatchModel;
    };
    await this.moveRoomSchedulesService.patch(userStore, param.urlPatam.startDatetime, param.urlPatam.roomId, param.urlPatam.residentId, param.requestBody);

    this.isBusy = false;
    this.showMoveRoomScheduleUpdateSuccessModal = true;

    this.closeMoveRoomScheduleTab(1, true);

    return;
  }

  /**
   * ### タブ数変更イベント
   *
   * - 動的に追加したタブの増減で呼び出しされる
   * - タブを追加した場合、タブの子コンポーネント（実際の機能を持つコンポーネント）の_nameが追加されたタブ名と同一であればアクティブにする
   */
  public changedTab(currentTabs: Array<BTab>): void {
    // タブ数が5以上の場合はタブの追加なので、最終タブに遷移する
    if (5 < currentTabs.length && currentTabs[currentTabs.length - 1]) {
      currentTabs[currentTabs.length - 1].activate();
    }

    return;
  }

  /**
   * ### 仮押さえ／転床の登録、編集タブの「x」ボタン押下イベント
   *
   * - 削除対象のタブをタブから除外する
   * - moveMoveRoomScheduleListTabがtrueの場合は転床／仮押さえ一覧タブへ移動する
   */
  public closeMoveRoomScheduleTab(tabNo: number, moveMoveRoomScheduleListTab: boolean): void {
    let newTabs: Array<Tab> = [];

    for (const tab of this.tabs) {
      if (tab.no != tabNo) {
        newTabs.push(tab);
      }
    }

    if (moveMoveRoomScheduleListTab == true) {
      this.tabIndex = 3;
    }

    this.tabs = newTabs;

    return;
  }

  /**
   * ### 「空室情報の公開先」機能使用可否の更新確認モーダルを表示する
   */
  public confirmUpdateVacancyFunction(members: Array<OtherUserGroupModel>): void {
    this.updateVacancyFunctionTargetMembers = members;
    this.showVacancyFunctionUpdateModal = true;

    return;
  }

  /**
   * ### 「空室情報の公開先」機能使用可否を更新する
   */
  public async updateVacancyFunction(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    const updateModels: Array<OtherUserGroupModel> = this.updateVacancyFunctionTargetMembers as Array<OtherUserGroupModel>;

    const updateModelsList: Array<Array<OtherUserGroupModel>> = this.sliceUpdateModels(updateModels, 50);
    for (const models of updateModelsList) {
      let promises: Array<Promise<void>> = [];

      for (const model of models) {
        const requestBody: OtherUserGroupMembersPatchModel = this.createMemberPatchModel(model);
        promises.push(this.otherUserGroupMembersService.patch(userStore, model.id, model.memberId, requestBody));
      }

      await Promise.all(promises);
    }

    this.showVacancyFunctionUpdateSuccessModal = true;
    this.isBusy = false;

    return;
  }

  /**
   * ### リクエストを並列実行するが、chromeの同時リクエスト数に上限があるので分割する
   */
  public sliceUpdateModels(updateModels: Array<OtherUserGroupModel>, sliceNum: number): Array<Array<OtherUserGroupModel>> {
    let updateModelsList: Array<Array<OtherUserGroupModel>> = [];

    for (let i: number = 0; i < Math.ceil(updateModels.length / sliceNum); i++) {
      const start: number = i * sliceNum;
      const end: number = start + sliceNum;
      const slicedUpdateModels: Array<OtherUserGroupModel> = updateModels.slice(start, end);
      updateModelsList.push(slicedUpdateModels);
    }
    return updateModelsList;
  }

  /**
   * ### 施設関係者グループメンバー更新リクエストボディを作成する
   */
  public createMemberPatchModel(member: OtherUserGroupModel): OtherUserGroupMembersPatchModel {
    return {
      groupId: member.id,
      memberName: member.memberName,
      memberTel: member.memberTel,
      memberOrganization: member.memberOrganization,
      otherUserPermittedFunctions: member.otherUserPermittedFunctions,
      updated: member.updated
    };
  }

  /**
   * ### 利用者編集画面へ遷移する
   *
   * - 利用者編集画面から戻ってきた際に「入退所・送迎予定」タブを開くためにタブ位置をstoreに保存する
   */
  public gotoResidentEdit(item: ResidentInOutScheduleTableItem): void {
    this.$router.push({ name: PcUrl.RoomScheduleResidentEdit, params: { id: `${item.id}`, type: "1" } });
    return;
  }

  /**
   * ### 空室／空床情報の設定画面へ遷移する
   */
  public gotoSetting(): void {
    this.$router.push({ name: PcUrl.RoomScheduleSetting, params: { type: "0" } });
    return;
  }

  /**
   * ### 通知ボタン押下イベント
   */
  public onNotification(): void {
    this.showNotificationRoomScheduleModal = true;
    return;
  }

  /**
   * ### 空室／空床情報の更新通知を送信する
   */
  public async notificationRoomSchedule(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "送信中...";

    await this.notificationRoomSchedulesService.notifyByLine(userStore);
    await this.notificationRoomSchedulesService.notifyByMail(userStore);
    this.showNotificationRoomScheduleSuccessModal = true;

    this.isBusy = false;

    return;
  }

  /**
   * ### 各タブの画面情報再作成
   */
  public async refreshTabs(): Promise<void> {
    const promises: Array<Promise<void>> = [];

    if (this.residentInOutScheduleListTab) {
      promises.push(this.residentInOutScheduleListTab.refresh());
    }
    if (this.normalStayRoomScheduleTab) {
      promises.push(this.normalStayRoomScheduleTab.refresh());
    }
    if (this.shortStayRoomScheduleTab) {
      promises.push(this.shortStayRoomScheduleTab.refresh());
    }
    if (this.moveRoomScheduleListTab) {
      promises.push(this.moveRoomScheduleListTab.refresh());
    }
    if (this.otherUserGroupMemberVacancyEditTab) {
      promises.push(this.otherUserGroupMemberVacancyEditTab.refresh());
    }

    await Promise.all(promises);

    return;
  }

  beforeRouteLeave(to: Route, from: Route, next: Function) {
    // 画面を離れる前に現在のフィルタリング条件をVuexに保存
    if (this.$options.name) {
      this.residentInOutScheduleListTab.setFilterSetting();
      this.normalStayRoomScheduleTab.setFilterSetting();
      this.shortStayRoomScheduleTab.setFilterSetting();
    }
    next();
  }
}

interface Tab {
  /** タブ識別No */
  no: TabNo;
  /** タブタイトル */
  title: string;
}

enum TabNo {
  MOVE_ROOM_SCHEDULE_REGISTER = 0,
  MOVE_ROOM_SCHEDULE_EDIT = 1
}
</script>

<style lang="scss" scoped>
#room-schedule-list {
  .box-header {
    .title {
      font-size: 22px;
      font-weight: bold;
    }
  }
  // タブの高さを指定してレイアウト崩れを防ぐ
  .tab-title {
    max-height: 24px;
  }
  .notification-btn,
  .setting-btn {
    width: 100px;
    min-width: 100px;
  }
}
</style>
